import type { ParticipantBehaviour, ParticipantWidgetLayoutItem } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaExpandArrowsAlt, FaPen, FaTrash } from 'react-icons/fa';
import { showModal } from '../../../../components/generic/Modal';
import { useDelayedLoading } from '../../../../hooks/useDelayedLoading';
import type { UseGridReturn } from '../../hooks/useGrid';
import { TableWidgetEditModal } from '../table/TableWidgetEditModal';
import { TableWidgetViewModal } from '../table/TableWidgetViewModal';
import { TimeSeriesWidgetViewModal } from '../timeseries/TimeSeriesWidgetViewModal';
import { TimeSeriesWidgetEditModal } from '../timeseries/TimeseriesWidgetEditModal';
import { ParticipantWidgetDeleteModal } from './ParticipantWidgetDeleteModal';
import { TableGridItem } from './TableGridItem';
import { TimeSeriesGridItem } from './TimeSeriesGridItem';

interface ParticipantDataGridItemProps {
  item: ParticipantWidgetLayoutItem;
  participantId: string;
  participantBehaviours: ParticipantBehaviour[];
  grid: UseGridReturn<ParticipantWidgetLayoutItem>;
  isDisabled?: boolean;
}

export const ParticipantDataGridItem = (props: ParticipantDataGridItemProps) => {
  const { item, participantBehaviours, participantId, grid, isDisabled } = props;

  // Introduce a fake loading delay to simulate loading.
  // Echarts seems to glitch if load too quickly
  const isSkeletonLoading = useDelayedLoading(1);

  const onRemove = () => {
    showModal(ParticipantWidgetDeleteModal, {
      onDelete: () => grid.onRemove(item.id),
    });
  };

  const onEdit = (item: ParticipantWidgetLayoutItem) => {
    switch (item.widget.__typename) {
      case 'TimeSeriesChartWidget': {
        showModal(TimeSeriesWidgetEditModal, {
          participantId,
          widget: item.widget,
          onEdit: (widget) => grid.onEdit({ ...item, widget }),
        });
        break;
      }

      case 'TableWidget': {
        showModal(TableWidgetEditModal, {
          participantId,
          widget: item.widget,
          onEdit: (widget) => grid.onEdit({ ...item, widget }),
        });
      }
    }
  };

  const onMaximize = (item: ParticipantWidgetLayoutItem) => {
    switch (item.widget.__typename) {
      case 'TimeSeriesChartWidget': {
        return showModal(TimeSeriesWidgetViewModal, {
          participantId,
          widget: item.widget,
          startDate: grid.startDate,
          endDate: grid.endDate,
        });
      }
      case 'TableWidget': {
        return showModal(TableWidgetViewModal, {
          participantId,
          widget: item.widget,
          startDate: grid.startDate,
          endDate: grid.endDate,
        });
      }
    }
  };

  const content = P.run(() => {
    switch (item.widget.__typename) {
      case 'TimeSeriesChartWidget':
        return (
          <TimeSeriesGridItem
            widget={item.widget}
            participantBehaviours={participantBehaviours}
            startDate={grid.startDate}
            endDate={grid.endDate}
            onStartDateChange={grid.onStartDateChange}
            onEndDateChange={grid.onEndDateChange}
          />
        );
      case 'TableWidget':
        return (
          <TableGridItem
            widget={item.widget}
            participantBehaviours={participantBehaviours}
            startDate={grid.startDate}
            endDate={grid.endDate}
          />
        );
      default:
        return <ChakraV3.Stack>Not implemented yet - {item.widget.__typename}</ChakraV3.Stack>;
    }
  });

  return (
    <ChakraV3.Stack w='full' h='full' gap={0}>
      <ChakraV3.HStack w='full' px={4} py={2} cursor='move' className='grid-stack-item-draghandle'>
        <ChakraV3.Heading size='md' fontWeight='bold'>
          {item.widget.title}
        </ChakraV3.Heading>
        <ChakraV3.Spacer />
        <ChakraV3.IconButton
          aria-label='Maximize'
          onClick={() => onMaximize(item)}
          variant='ghost'
          size='2xs'
          zIndex={10}
          color='gray.500'
        >
          <FaExpandArrowsAlt />
        </ChakraV3.IconButton>
        <ChakraV3.IconButton
          aria-label='Edit'
          onClick={() => onEdit(item)}
          disabled={isDisabled}
          variant='ghost'
          size='2xs'
          zIndex={10}
          color='gray.500'
        >
          <FaPen />
        </ChakraV3.IconButton>
        <ChakraV3.IconButton
          aria-label='Remove'
          onClick={onRemove}
          disabled={isDisabled}
          variant='ghost'
          size='2xs'
          zIndex={10}
          color='gray.500'
        >
          <FaTrash />
        </ChakraV3.IconButton>
      </ChakraV3.HStack>
      <ChakraV3.Separator />
      <ChakraV3.Stack
        w='full'
        h='full'
        opacity={isSkeletonLoading ? 0 : 1}
        transition='opacity 0.5s ease-in-out'
      >
        {content}
      </ChakraV3.Stack>
    </ChakraV3.Stack>
  );
};
